import React, { useRef, useState } from "react";
import { message, Modal } from "antd";
import { useStaffData } from "../hooks/useStaffData";
import { useStaffModalManager } from "../hooks/useStaffModalManager";

import {
  useAddPermissionMutation,
  useDeletePermissionMutation,
  useUpdatePermissionMutation,
} from "@/services/api/permissions/permissionApi";
import TabbedStaffTable from "./tables/TabbedStaffTable";
import ModalManager from "./modals/ModalManager";
import {
  useAddRolePermissionMutation,
  useDeleteRolePermissionMutation,
  useUpdateRolePermissionMutation,
} from "@/services/api/rolePermission/rolePermissionApi";
import {
  useAssignUsersToRoleMutation,
  useLazyGetUsersAssignedToRoleQuery,
} from "@/services/api/roles/rolesApi";
import Loader from "@/components/UI/Loader/Loader";
import {
  useAddEmployeeMutation,
  useUpdateEmployeeMutation,
  useDeleteEmployeeMutation, // ← added missing import
} from "@/services/api/user/employeeApi";

const StaffRoleTable = ({ activeTab, onTabChange }) => {
  const employeeFormRef = useRef();

  // API mutations
  const [addEmployee, { isLoading: isAddingEmployee }] =
    useAddEmployeeMutation();
  const [updateEmployee, { isLoading: isUpdatingEmployee }] =
    useUpdateEmployeeMutation();
  const [deleteEmployee, { isLoading: isDeletingEmployee }] =
    useDeleteEmployeeMutation(); // ← now properly initialised
  const [addRole, { isLoading: isAddingRole }] = useAddRolePermissionMutation();
  const [updateRole, { isLoading: isUpdatingRole }] =
    useUpdateRolePermissionMutation();
  const [addPermission, { isLoading: isAddingPermission }] =
    useAddPermissionMutation();
  const [updatePermission, { isLoading: isUpdatingPermission }] =
    useUpdatePermissionMutation();
  const [deletePermission, { isLoading: isDeletingPermission }] =
    useDeletePermissionMutation();
  const [deleteRole, { isLoading: isDeletingRole }] =
    useDeleteRolePermissionMutation();
  const [assignUsersToRole, { isLoading: isAssigningUsers }] =
    useAssignUsersToRoleMutation();
  const [getUsersAssignedToRole] = useLazyGetUsersAssignedToRoleQuery();

  // Use custom hook with pagination support
  const {
    employeeData,
    rolesData,
    permissionsData,
    rawEmployeeData,
    rawRolesData,
    isLoadingEmployees,
    isLoadingRoles,
    isLoadingPermissions,
    isFetchingEmployees,
    isFetchingRoles,
    isFetchingPermissions,
    isEmployeesError,
    isRolesError,
    isPermissionsError,
    employeesError,
    rolesError,
    permissionsError,
    permissionsPaginationInfo,
    rolesPaginationInfo,
    employeesPaginationInfo,
    handlePermissionsPageChange,
    handlePermissionsPageSizeChange,
    handleRolesPageChange,
    handleRolesPageSizeChange,
    handleEmployeesPageChange,
    handleEmployeesPageSizeChange,
    handleEmployeesSearch,
    handleRolesSearch,
    handlePermissionsSearch,
  } = useStaffData();

  const {
    modals,
    modalData,
    isSubmitting,
    setIsSubmitting,
    handleAddEmployee,
    handleEditEmployee,
    handleViewEmployee,
    handleBulkImport,
    handleAddRole,
    handleEditRole,
    handleViewRole,
    handleViewAssignedUsers,
    handleViewPermission,
    handleEditPermission,
    handleImportRoles,
    handleAddPermission,
    handleImportPermissions,
    handleOpenAddNewUsers,
    handleCloseAddEmployeeModal,
    handleCloseBulkImportModal,
    handleCloseAddRoleModal,
    handleCloseImportRolesModal,
    handleCloseAddPermissionModal,
    handleCloseImportPermissionsModal,
    handleCloseAssignUsersModal,
    handleCloseAddNewUsersModal,
  } = useStaffModalManager();

  // ── Employee Submit (Add / Edit) ─────────────────────────────────────────
  const handleEmployeeSubmit = async (formValues) => {
    try {
      const { modalMode, selectedOriginalEmployee } = modalData;

      let result;
      if (modalMode === "edit" && selectedOriginalEmployee?.id) {
        result = await updateEmployee({
          id: selectedOriginalEmployee.id,
          ...formValues,
        }).unwrap();
        message.success("Employee updated successfully!");
      } else {
        result = await addEmployee(formValues).unwrap();
        message.success("Employee added successfully!");
      }

      handleCloseAddEmployeeModal();
    } catch (error) {
      console.error(
        `Error ${modalData.modalMode === "edit" ? "updating" : "adding"} employee:`,
        error,
      );
      const action = modalData.modalMode === "edit" ? "update" : "add";
      const errorMessage =
        error?.data?.message ||
        error?.message ||
        `Failed to ${action} employee.`;
      message.error(errorMessage);
      throw error;
    }
  };

  // ── Delete Employee ──────────────────────────────────────────────────────
  const handleDeleteEmployee = (id, name) => {
    Modal.confirm({
      title: "Delete Employee",
      content: `Are you sure you want to delete ${name}? This action cannot be undone.`,
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      centered: true,
      onOk: async () => {
        try {
          await deleteEmployee(id).unwrap();
          message.success(`${name} deleted successfully`);
        } catch (error) {
          console.error("Error deleting employee:", error);
          message.error(error?.data?.message || "Failed to delete employee");
        }
      },
    });
  };

  // ── Role Submit (Add / Edit) ─────────────────────────────────────────────
  const handleRoleSubmit = async (roleData) => {
    setIsSubmitting(true);
    try {
      if (roleData.id) {
        await updateRole({
          role_id: roleData.id,
          role_name: roleData.role_name,
          description: roleData.description,
          permissions: roleData.permissions,
          status: roleData.status,
        }).unwrap();
        message.success("Role updated successfully");
      } else {
        await addRole(roleData).unwrap();
        message.success("Role created successfully");
      }
      handleCloseAddRoleModal();
    } catch (error) {
      console.error("Error with role operation:", error);
      message.error(
        error?.data?.message ||
        `Failed to ${roleData.id ? "update" : "create"} role`,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Permission Submit (Add / Edit) ───────────────────────────────────────
  const handlePermissionSubmit = async (permissionData) => {
    setIsSubmitting(true);
    try {
      if (permissionData.id) {
        await updatePermission({
          id: permissionData.id,
          requestData: {
            name: permissionData.name,
            description: permissionData.description,
          },
        }).unwrap();
        message.success("Permission updated successfully");
      } else {
        await addPermission(permissionData).unwrap();
        message.success("Permission created successfully");
      }
      handleCloseAddPermissionModal();
    } catch (error) {
      console.error("Error with permission operation:", error);
      message.error(
        error?.data?.message ||
        `Failed to ${permissionData.id ? "update" : "create"} permission`,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Delete Permission ────────────────────────────────────────────────────
  const handleRemovePermission = async (record) => {
    try {
      await deletePermission(record.id).unwrap();
      message.success("Permission deleted successfully");
    } catch (error) {
      console.error("Error deleting permission:", error);
      message.error(error?.data?.message || "Failed to delete permission");
    }
  };

  // ── Delete Role ──────────────────────────────────────────────────────────
  const handleDeleteRole = async (record) => {
    try {
      await deleteRole(record.id).unwrap();
      message.success("Role deleted successfully");
    } catch (error) {
      console.error("Error deleting Role:", error);
      message.error(error?.data?.message || "Failed to delete role");
    }
  };

  // ── Assign Users to Role ─────────────────────────────────────────────────
  const handleAssignUsers = async (payload) => {
    const payloadData = {
      roleId: payload.roleId,
      requestData: { user_ids: payload.employeeIds },
    };
    try {
      await assignUsersToRole(payloadData).unwrap();
      message.success("Users assigned successfully");
      handleCloseAssignUsersModal();
    } catch (error) {
      console.error("Error assigning users:", error);
      message.error(error?.data?.message || "Failed to assign users");
      throw error;
    }
  };

  // ── Add New Users from modal ─────────────────────────────────────────────
  const handleAddNewUsers = async (userIds) => {
    if (!modalData.selectedRole?.id && !modalData.selectedRole?.roleId) {
      message.error("No role selected");
      return;
    }

    try {
      const payload = {
        roleId: modalData.selectedRole.roleId || modalData.selectedRole.id,
        employeeIds: userIds,
      };
      await assignUsersToRole(payload).unwrap();
      message.success(`${userIds.length} user(s) added successfully`);
    } catch (error) {
      console.error("Error adding new users:", error);
      message.error(error?.data?.message || "Failed to add users");
      throw error;
    }
  };

  // ── Fetch assigned users for a role ─────────────────────────────────────
  const handleFetchAssignedUsers = async (roleId) => {
    const result = await getUsersAssignedToRole(roleId).unwrap();
    return result.role?.users || [];
  };

  // ── Wrappers (resolve raw records before passing to modal) ───────────────
  const handleDownloadProfile = (record) =>
    console.log("Download profile:", record);

  const handleViewRoleWrapper = (record) => {
    const originalRole = rawRolesData?.find((role) => role.id === record.id);
    handleViewRole(originalRole || record);
  };

  const handleEditRoleWrapper = (record) => {
    const originalRole = rawRolesData?.find((role) => role.id === record.id);
    handleEditRole(originalRole || record);
  };

  const handleAssignUsersWrapper = (record) => {
    const originalRole = rawRolesData?.find((role) => role.id === record.id);
    handleViewAssignedUsers(originalRole || record);
  };

  const handleViewPermissionWrapper = (record) => handleViewPermission(record);
  const handleEditPermissionWrapper = (record) => handleEditPermission(record);

  const handleManagePermissions = (record) =>
    console.log("Manage permissions:", record);

  // ── Bulk handlers ────────────────────────────────────────────────────────
  const handleBulkDeleteEmployee = (keys) =>
    console.log("Bulk delete employees:", keys);
  const handleBulkEditEmployee = (keys) =>
    console.log("Bulk edit employees:", keys);
  const handleBulkDeleteRole = (keys) =>
    console.log("Bulk delete roles:", keys);
  const handleBulkEditRole = (keys) => console.log("Bulk edit roles:", keys);
  const handleBulkDeletePermission = (keys) =>
    console.log("Bulk delete permissions:", keys);
  const handleBulkEditPermission = (keys) =>
    console.log("Bulk edit permissions:", keys);

  // ── Loading state ────────────────────────────────────────────────────────
  if (isLoadingEmployees || isLoadingRoles || isLoadingPermissions) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">
          <Loader />
        </div>
      </div>
    );
  }

  // ── Error state ──────────────────────────────────────────────────────────
  if (isEmployeesError || isRolesError || isPermissionsError) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-md text-red-600 text-center">
          Error loading data: <br />
          {(isEmployeesError
            ? employeesError?.message
            : isRolesError
              ? rolesError?.message
              : permissionsError?.message) ||
            "Please check internet connection"}
        </div>
      </div>
    );
  }

  return (
    <div>
      <TabbedStaffTable
        employeeData={employeeData}
        rolesData={rolesData}
        permissionsData={permissionsData}
        rawEmployeeData={rawEmployeeData}
        isFetchingEmployees={isFetchingEmployees}
        isFetchingRoles={isFetchingRoles}
        isFetchingPermissions={isFetchingPermissions}
        activeTab={activeTab}
        onTabChange={onTabChange}
        onAddEmployee={handleAddEmployee}
        onBulkImport={handleBulkImport}
        onEditEmployee={handleEditEmployee}
        onViewEmployee={handleViewEmployee}
        onDeleteEmployee={handleDeleteEmployee} // ← wired to table
        isDeletingEmployee={isDeletingEmployee} // ← pass loading state so table can disable the button
        onDownloadProfile={handleDownloadProfile}
        onAddRole={handleAddRole}
        onImportRoles={handleImportRoles}
        onViewRole={handleViewRoleWrapper}
        onEditRole={handleEditRoleWrapper}
        onDeleteRole={handleDeleteRole}
        onAssignUsers={handleAssignUsersWrapper}
        onManagePermissions={handleManagePermissions}
        onAddPermission={handleAddPermission}
        onRemovePermission={handleRemovePermission}
        onEditPermission={handleEditPermissionWrapper}
        onViewPermission={handleViewPermissionWrapper}
        onImportPermissions={handleImportPermissions}
        onBulkDeleteEmployee={handleBulkDeleteEmployee}
        onBulkEditEmployee={handleBulkEditEmployee}
        onBulkDeleteRole={handleBulkDeleteRole}
        onBulkEditRole={handleBulkEditRole}
        onBulkDeletePermission={handleBulkDeletePermission}
        onBulkEditPermission={handleBulkEditPermission}
        employeesPaginationInfo={employeesPaginationInfo}
        onEmployeesPageChange={handleEmployeesPageChange}
        onEmployeesPageSizeChange={handleEmployeesPageSizeChange}
        rolesPaginationInfo={rolesPaginationInfo}
        onRolesPageChange={handleRolesPageChange}
        onRolesPageSizeChange={handleRolesPageSizeChange}
        permissionsPaginationInfo={permissionsPaginationInfo}
        onPermissionsPageChange={handlePermissionsPageChange}
        onPermissionsPageSizeChange={handlePermissionsPageSizeChange}
        onEmployeesSearch={handleEmployeesSearch}
        onRolesSearch={handleRolesSearch}
        onPermissionsSearch={handlePermissionsSearch}
      />

      <ModalManager
        modals={modals}
        modalData={modalData}
        isSubmitting={isSubmitting}
        setIsSubmitting={setIsSubmitting}
        onCloseAddEmployeeModal={handleCloseAddEmployeeModal}
        onCloseBulkImportModal={handleCloseBulkImportModal}
        onCloseAddRoleModal={handleCloseAddRoleModal}
        onCloseImportRolesModal={handleCloseImportRolesModal}
        onCloseAddPermissionModal={handleCloseAddPermissionModal}
        onCloseImportPermissionsModal={handleCloseImportPermissionsModal}
        onCloseAssignUsersModal={handleCloseAssignUsersModal}
        onCloseAddNewUsersModal={handleCloseAddNewUsersModal}
        onOpenAddNewUsers={handleOpenAddNewUsers}
        onEmployeeSubmit={handleEmployeeSubmit}
        onRoleSubmit={handleRoleSubmit}
        onPermissionSubmit={handlePermissionSubmit}
        onAssignUsers={handleAssignUsers}
        onAddNewUsers={handleAddNewUsers}
        onFetchAssignedUsers={handleFetchAssignedUsers}
        employeeFormRef={employeeFormRef}
        rawEmployeeData={rawEmployeeData}
        allUsers={rawEmployeeData}
        isLoadingUsers={isLoadingEmployees}
      />
    </div>
  );
};

export default StaffRoleTable;
